// ---------------------------------------------------------------------------
// StickySection Renderer (editor key: StickySection)
// ---------------------------------------------------------------------------
// Wrapper that pins a heading + body block to the top (or bottom) of the
// viewport via CSS `position:sticky`. We emit a section with a class-driven
// `position` modifier; offsets and shadow intensity stay in the design
// system rather than inline styles. Body copy supports a single paragraph
// of escaped text; richer content belongs in a container/zone block. If
// neither heading nor body is provided we emit a HTML comment so the
// publisher can spot empty drops.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

interface StickySectionProps {
  heading?: string;
  body?: string;
  position?: "top" | "bottom";
  offset?: "none" | "small" | "medium" | "large";
}

const ALLOWED_POSITIONS = new Set(["top", "bottom"]);
const ALLOWED_OFFSETS = new Set(["none", "small", "medium", "large"]);

function normalizePosition(value: unknown): "top" | "bottom" {
  if (typeof value === "string" && ALLOWED_POSITIONS.has(value)) {
    return value as "top" | "bottom";
  }
  return "top";
}

function normalizeOffset(value: unknown): string {
  if (typeof value === "string" && ALLOWED_OFFSETS.has(value)) return value;
  return "none";
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as StickySectionProps;

  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  const body = typeof p.body === "string" ? p.body.trim() : "";
  if (!heading && !body) {
    return "<!-- sticky-section missing content -->";
  }

  const position = normalizePosition(p.position);
  const offset = normalizeOffset(p.offset);

  const headingHtml = heading
    ? `\n    <h2 class="ease-sticky-section__heading">${text(heading)}</h2>`
    : "";
  const bodyHtml = body
    ? `\n    <p class="ease-sticky-section__body">${text(body)}</p>`
    : "";

  return `<section class="ease-section ease-sticky-section ease-sticky-section--${attr(position)} ease-sticky-section--offset-${attr(offset)}" data-ease-position="${attr(position)}" data-ease-offset="${attr(offset)}">
  <div class="ease-sticky-section__inner">${headingHtml}${bodyHtml}
  </div>
</section>`;
}

registerRenderer("sticky-section", { render });
